import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith, take, withLatestFrom } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { SkuQuantityType } from 'ish-core/models/product/product.model';
import { SelectOption } from 'ish-core/models/select-option/select-option.model';
import { whenTruthy } from 'ish-core/utils/operators';

import { OrderTemplate, OrderTemplateHeader } from '../models/order-template/order-template.model';
import { SelectOrderTemplateModalComponent } from '../shared/select-order-template-modal/select-order-template-modal.component';
import {
  addBasketToNewOrderTemplate,
  addProductToNewOrderTemplate,
  addProductToOrderTemplate,
  addProductsToNewOrderTemplate,
  addProductsToOrderTemplate,
  createOrderTemplate,
  deleteOrderTemplate,
  getAllOrderTemplates,
  getOrderTemplateError,
  getOrderTemplateLoading,
  getSelectedOrderTemplateDetails,
  getSelectedOrderTemplateOutOfStockItems,
  loadOrderTemplates,
  moveItemToOrderTemplate,
  removeItemFromOrderTemplate,
  updateOrderTemplate,
} from '../store/order-template';

@Injectable({ providedIn: 'root' })
export class OrderTemplatesFacade {
  constructor(private store: Store, private accountFacade: AccountFacade) {}

  orderTemplates$: Observable<OrderTemplate[]> = this.store.pipe(select(getAllOrderTemplates));
  currentOrderTemplate$: Observable<OrderTemplate> = this.store.pipe(select(getSelectedOrderTemplateDetails));
  currentOrderTemplateOutOfStockItems$: Observable<string[]> = this.store.pipe(
    select(getSelectedOrderTemplateOutOfStockItems)
  );
  orderTemplateLoading$: Observable<boolean> = this.store.pipe(select(getOrderTemplateLoading));
  orderTemplateError$: Observable<HttpError> = this.store.pipe(select(getOrderTemplateError));

  orderTemplatesSelectOptions$(filterCurrent = true): Observable<SelectOption[]> {
    return this.orderTemplates$.pipe(
      startWith([] as OrderTemplate[]),
      /* Commented for DIAS Customization
      map(orderTemplates =>
        orderTemplates.map(orderTemplate => ({
          value: orderTemplate.id,
          label: orderTemplate.title,
        }))
      */
      /* DIAS Customization Start */
      map(
        orderTemplates =>
          orderTemplates
            .filter(orderTemplates => orderTemplates.editable)
            .map(orderTemplate => ({
              value: orderTemplate.id,
              label: orderTemplate.title,
            }))
        /* DIAS Customization End */
      ),
      withLatestFrom(this.currentOrderTemplate$),
      map(([orderTemplateOptions, currentOrderTemplate]) => {
        if (filterCurrent && currentOrderTemplate) {
          return orderTemplateOptions.filter(option => option.value !== currentOrderTemplate.id);
        }
        return orderTemplateOptions;
      })
    );
  }

  loadOrderTemplate() {
    this.store.dispatch(loadOrderTemplates());
  }

  addOrderTemplate(orderTemplate: OrderTemplateHeader): void | HttpError {
    this.store.dispatch(createOrderTemplate({ orderTemplate }));
  }

  addBasketToNewOrderTemplate(orderTemplate: OrderTemplateHeader): void | HttpError {
    this.store.dispatch(addBasketToNewOrderTemplate({ orderTemplate }));
  }

  deleteOrderTemplate(id: string): void {
    this.store.dispatch(deleteOrderTemplate({ orderTemplateId: id }));
  }

  updateOrderTemplate(orderTemplate: OrderTemplate): void {
    this.store.dispatch(updateOrderTemplate({ orderTemplate }));
  }

  addProductToNewOrderTemplate(title: string, sku: string, quantity?: number): void {
    this.store.dispatch(addProductToNewOrderTemplate({ title, sku, quantity }));
  }

  addProductToOrderTemplate(orderTemplateId: string, sku: string, quantity?: number): void {
    this.store.dispatch(addProductToOrderTemplate({ orderTemplateId, sku, quantity }));
  }

  moveItemToOrderTemplate(
    sourceOrderTemplateId: string,
    targetOrderTemplateId: string,
    sku: string,
    quantity: number
  ): void {
    this.store.dispatch(
      moveItemToOrderTemplate({
        source: { id: sourceOrderTemplateId },
        target: { id: targetOrderTemplateId, sku, quantity },
      })
    );
  }

  moveItemToNewOrderTemplate(sourceOrderTemplateId: string, title: string, sku: string, quantity: number): void {
    this.store.dispatch(
      moveItemToOrderTemplate({ source: { id: sourceOrderTemplateId }, target: { title, sku, quantity } })
    );
  }

  removeProductFromOrderTemplate(orderTemplateId: string, sku: string): void {
    this.store.dispatch(removeItemFromOrderTemplate({ orderTemplateId, sku }));
  }

  /* DIAS Customizations Start */

  currentUsersOrderTemplates$(): Observable<OrderTemplate[]> {
    return this.orderTemplates$.pipe(
      map(orderTemplates => orderTemplates.filter(orderTemplate => orderTemplate.editable))
    );
  }

  addProductsToNewOrderTemplate(title: string, products: SkuQuantityType[]): void {
    this.store.dispatch(addProductsToNewOrderTemplate({ title, products }));
  }

  addProductsToOrderTemplate(orderTemplateId: string, products: SkuQuantityType[]): void {
    this.store.dispatch(addProductsToOrderTemplate({ orderTemplateId, products }));
  }

  openModalAfterLogin(modal: SelectOrderTemplateModalComponent) {
    this.accountFacade.isLoggedIn$.pipe(whenTruthy(), debounceTime(2000), take(1)).subscribe(() => modal.show());
  }
  /* DIAS Customizations End */
}
